import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '@/context/AuthContext';
import axios from 'axios';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Plus, Store, Edit, Power, Trash2, Search } from 'lucide-react';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const ManageStores = () => {
  const { token, isSuperAdmin } = useAuth();
  const navigate = useNavigate();
  const [stores, setStores] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [selectedStore, setSelectedStore] = useState(null);
  const [formData, setFormData] = useState({
    name: '',
    address: '',
    contact: '',
    email: '',
    status: 'active'
  });

  useEffect(() => {
    if (!isSuperAdmin()) {
      navigate('/');
      return;
    }
    fetchStores();
  }, []);

  const fetchStores = async () => {
    try {
      const response = await axios.get(`${API}/admin/stores`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setStores(response.data);
    } catch (error) {
      toast.error('Failed to fetch stores');
    } finally {
      setLoading(false);
    }
  };

  const handleCreateStore = async () => {
    try {
      await axios.post(`${API}/admin/stores`, {
        name: formData.name,
        address: formData.address || null,
        contact: formData.contact || null,
        email: formData.email || null
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Store created successfully');
      setShowCreateModal(false);
      resetForm();
      fetchStores();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to create store');
    }
  };

  const handleUpdateStore = async () => {
    try {
      await axios.put(`${API}/admin/stores/${selectedStore.id}`, {
        name: formData.name,
        address: formData.address || null,
        contact: formData.contact || null,
        email: formData.email || null,
        status: formData.status
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Store updated successfully');
      setShowEditModal(false);
      resetForm();
      fetchStores();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to update store');
    }
  };

  const handleToggleStatus = async (store) => {
    const newStatus = store.status === 'active' ? 'inactive' : 'active';
    try {
      await axios.put(`${API}/admin/stores/${store.id}`, {
        status: newStatus
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success(`Store ${newStatus === 'active' ? 'enabled' : 'disabled'} successfully`);
      fetchStores();
    } catch (error) {
      toast.error('Failed to update store status');
    }
  };

  const handleDeleteStore = async (storeId) => {
    if (!window.confirm('Are you sure you want to delete this store? This action cannot be undone.')) return;
    
    try {
      await axios.delete(`${API}/admin/stores/${storeId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Store deleted successfully');
      fetchStores();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to delete store');
    }
  };

  const openEditModal = (store) => {
    setSelectedStore(store);
    setFormData({
      name: store.name,
      address: store.address || '',
      contact: store.contact || '',
      email: store.email || '',
      status: store.status
    });
    setShowEditModal(true);
  };

  const resetForm = () => {
    setFormData({
      name: '',
      address: '',
      contact: '',
      email: '',
      status: 'active'
    });
    setSelectedStore(null);
  };

  const filteredStores = stores.filter(store =>
    store.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
    store.email?.toLowerCase().includes(searchTerm.toLowerCase())
  );

  if (loading) {
    return (
      <div className="flex items-center justify-center h-full">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-indigo-600"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div className="relative flex-1 max-w-md">
          <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-slate-400 w-5 h-5" />
          <Input
            placeholder="Search stores..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="pl-10"
            data-testid="search-stores-input"
          />
        </div>
        <Button
          className="bg-indigo-600 hover:bg-indigo-700"
          onClick={() => { resetForm(); setShowCreateModal(true); }}
          data-testid="create-store-btn"
        >
          <Plus className="w-4 h-4 mr-2" />
          Add Store
        </Button>
      </div>

      {/* Stores Grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
        {filteredStores.map((store) => (
          <Card key={store.id} className="card-hover" data-testid={`store-card-${store.id}`}>
            <CardContent className="pt-6">
              <div className="flex items-start justify-between mb-4">
                <div className="flex items-center gap-3">
                  <div className={`p-2 rounded-lg ${store.status === 'active' ? 'bg-green-50' : 'bg-red-50'}`}>
                    <Store className={`w-5 h-5 ${store.status === 'active' ? 'text-green-600' : 'text-red-600'}`} />
                  </div>
                  <div>
                    <h3 className="font-semibold text-slate-900">{store.name}</h3>
                    <Badge className={store.status === 'active' ? 'bg-green-100 text-green-700' : 'bg-red-100 text-red-700'}>
                      {store.status}
                    </Badge>
                  </div>
                </div>
              </div>

              {store.email && (
                <p className="text-sm text-slate-500 mb-1">{store.email}</p>
              )}
              {store.contact && (
                <p className="text-sm text-slate-500 mb-1">{store.contact}</p>
              )}
              {store.address && (
                <p className="text-sm text-slate-400 mb-4">{store.address}</p>
              )}

              <div className="flex items-center gap-2 pt-4 border-t border-slate-100">
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => openEditModal(store)}
                  data-testid={`edit-store-${store.id}`}
                >
                  <Edit className="w-4 h-4 mr-1" />
                  Edit
                </Button>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => handleToggleStatus(store)}
                  className={store.status === 'active' ? 'text-amber-600 hover:text-amber-700' : 'text-green-600 hover:text-green-700'}
                  data-testid={`toggle-store-${store.id}`}
                >
                  <Power className="w-4 h-4 mr-1" />
                  {store.status === 'active' ? 'Disable' : 'Enable'}
                </Button>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => handleDeleteStore(store.id)}
                  className="text-red-600 hover:text-red-700"
                  data-testid={`delete-store-${store.id}`}
                >
                  <Trash2 className="w-4 h-4" />
                </Button>
              </div>
            </CardContent>
          </Card>
        ))}
      </div>

      {filteredStores.length === 0 && (
        <Card>
          <CardContent className="flex flex-col items-center justify-center py-12">
            <Store className="w-16 h-16 text-slate-300 mb-4" />
            <p className="text-slate-500 text-lg">No stores found</p>
            <p className="text-slate-400 text-sm">Create your first store to get started</p>
          </CardContent>
        </Card>
      )}

      {/* Create Store Modal */}
      <Dialog open={showCreateModal} onOpenChange={setShowCreateModal}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Create New Store</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div>
              <Label htmlFor="name">Store Name *</Label>
              <Input
                id="name"
                value={formData.name}
                onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                placeholder="Enter store name"
                data-testid="store-name-input"
              />
            </div>
            <div>
              <Label htmlFor="email">Email</Label>
              <Input
                id="email"
                type="email"
                value={formData.email}
                onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                placeholder="store@example.com"
                data-testid="store-email-input"
              />
            </div>
            <div>
              <Label htmlFor="contact">Contact Number</Label>
              <Input
                id="contact"
                value={formData.contact}
                onChange={(e) => setFormData({ ...formData, contact: e.target.value })}
                placeholder="Phone number"
                data-testid="store-contact-input"
              />
            </div>
            <div>
              <Label htmlFor="address">Address</Label>
              <Input
                id="address"
                value={formData.address}
                onChange={(e) => setFormData({ ...formData, address: e.target.value })}
                placeholder="Store address"
                data-testid="store-address-input"
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowCreateModal(false)}>
              Cancel
            </Button>
            <Button 
              className="bg-indigo-600 hover:bg-indigo-700" 
              onClick={handleCreateStore}
              disabled={!formData.name}
              data-testid="submit-create-store"
            >
              Create Store
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Edit Store Modal */}
      <Dialog open={showEditModal} onOpenChange={setShowEditModal}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Edit Store</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div>
              <Label htmlFor="edit-name">Store Name *</Label>
              <Input
                id="edit-name"
                value={formData.name}
                onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                placeholder="Enter store name"
                data-testid="edit-store-name-input"
              />
            </div>
            <div>
              <Label htmlFor="edit-email">Email</Label>
              <Input
                id="edit-email"
                type="email"
                value={formData.email}
                onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                placeholder="store@example.com"
                data-testid="edit-store-email-input"
              />
            </div>
            <div>
              <Label htmlFor="edit-contact">Contact Number</Label>
              <Input
                id="edit-contact"
                value={formData.contact}
                onChange={(e) => setFormData({ ...formData, contact: e.target.value })}
                placeholder="Phone number"
                data-testid="edit-store-contact-input"
              />
            </div>
            <div>
              <Label htmlFor="edit-address">Address</Label>
              <Input
                id="edit-address"
                value={formData.address}
                onChange={(e) => setFormData({ ...formData, address: e.target.value })}
                placeholder="Store address"
                data-testid="edit-store-address-input"
              />
            </div>
            <div>
              <Label htmlFor="edit-status">Status</Label>
              <Select 
                value={formData.status} 
                onValueChange={(value) => setFormData({ ...formData, status: value })}
              >
                <SelectTrigger data-testid="edit-store-status-select">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="active">Active</SelectItem>
                  <SelectItem value="inactive">Inactive</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowEditModal(false)}>
              Cancel
            </Button>
            <Button 
              className="bg-indigo-600 hover:bg-indigo-700" 
              onClick={handleUpdateStore}
              disabled={!formData.name}
              data-testid="submit-edit-store"
            >
              Save Changes
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default ManageStores;
